import React from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { openExternalUrl } from "@/utils/linkify";

/**
 * Apple 3.1.2 requires a functional Terms link on the paywall. Both pages are public
 * routes in this app (/terms-of-use, /privacy-policy, unauthenticated in App.tsx),
 * served by Lovable hosting at stayyoung.lovable.app — the canonical origin. The
 * older open.stayyoung.live only 307-redirects here, so linking it added a hop that
 * could break independently of the app; point straight at the origin instead.
 */
export const TERMS_URL = "https://stayyoung.lovable.app/terms-of-use";
export const PRIVACY_URL = "https://stayyoung.lovable.app/privacy-policy";
export const SUPPORT_EMAIL = "support@stayyoung.live";

/**
 * On native these MUST go through openExternalUrl (Capacitor Browser.open). A bare
 * `<a target="_blank">` to a cross-origin https host is silently swallowed by the
 * Capacitor WebView (see SettingsTab's cancel link, which had exactly this bug) —
 * so the paywall's legal links would look present but do nothing when the reviewer
 * taps them, which is a 3.1.2 rejection. The href stays on the anchor so the web
 * build, keyboard users and long-press/copy-link all keep working.
 */
function handleLegalLink(url: string) {
  return (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    void openExternalUrl(url);
  };
}

// Each link is a full 44px target; the row used to be three bare text-xs words
// a few pixels apart, so a thumb aimed at one often hit its neighbour.
const FOOTER_LINK = "inline-flex min-h-11 items-center px-2 underline";

/**
 * Apple requires Restore Purchases and both legal links to be reachable from the
 * paywall itself — including when no plan is on sale, since a member who already
 * paid may be looking at exactly that screen. Missing them is a routine rejection.
 */
export function PaywallFooterLinks({ onRestore, busy }: { onRestore: () => void; busy: boolean }) {
  return (
    <div className="mt-4 flex flex-wrap items-center justify-center gap-x-1 text-xs text-muted-foreground">
      <button type="button" onClick={onRestore} disabled={busy} className={FOOTER_LINK}>
        שחזור רכישות
      </button>
      <span aria-hidden="true">·</span>
      <a
        href={TERMS_URL}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleLegalLink(TERMS_URL)}
        className={FOOTER_LINK}
      >
        תנאי שימוש
      </a>
      <span aria-hidden="true">·</span>
      <a
        href={PRIVACY_URL}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleLegalLink(PRIVACY_URL)}
        className={FOOTER_LINK}
      >
        מדיניות פרטיות
      </a>
    </div>
  );
}

/**
 * No purchasable plans. This is the normal state when the kill-switch is off or
 * the products are pulled from sale — so it must read as "not now", not "error",
 * and must never offer an external purchase link (rule 3.1.1). It still gives a
 * way forward: retry (a flaky store lookup lands here too), restore, and a
 * support address that is not a purchase path.
 */
export function PaywallUnavailable({
  onRetry,
  onRestore,
  busy,
  notice,
}: {
  onRetry: () => void;
  onRestore: () => void;
  busy: boolean;
  notice: string | null;
}) {
  return (
    <div
      className="flex min-h-[60vh] flex-col items-center justify-center px-6 text-center"
      dir="rtl"
    >
      <h2 className="mb-2 text-xl font-bold text-foreground">המנוי אינו זמין כרגע</h2>
      <p className="max-w-sm text-muted-foreground">
        אפשר לנסות שוב עכשיו. אם זה חוזר, כתבו לנו ל-
        <a href={`mailto:${SUPPORT_EMAIL}`} className="font-medium text-foreground underline" dir="ltr">
          {SUPPORT_EMAIL}
        </a>
      </p>
      <Button variant="outline" className="mt-6 min-w-40" onClick={onRetry} disabled={busy}>
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "לנסות שוב"}
      </Button>
      {notice && (
        <p className="mt-4 text-sm text-muted-foreground" role="status">
          {notice}
        </p>
      )}
      <PaywallFooterLinks onRestore={onRestore} busy={busy} />
    </div>
  );
}
